import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSOES_SUBSISTEMAS, permissaoDoSubsistema } from '@malgoos/shared';
import { Selo } from '../../componentes/base';
import { modulosWeb } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';
import { useSubsistema } from '../../nucleo/subsistemas';
import { alternarAcao, alternarGrupo, chavesDoRotuloDaAcao, alternarPaginaNoGrupo, estadoDoGrupo, grupoMarcado, gruposDaArvore, paginaMarcadaNoGrupo, resumoDoAcesso, tambemEm, type EstadoAcesso, type GrupoArvore } from './arvore-acesso';
import type { Permissao } from './tipos';

/** As páginas de cada módulo instalado, pelas abas do web (a mesma chave `<slug>.<aba>` do servidor). */
export function usePaginasPorModulo(): Map<string, string[]> {
  const s = useSessao();
  return useMemo(() => new Map(modulosWeb.filter((m) => s.instalado(m.slug) && m.abas.length).map((m) => [m.slug, m.abas.map((a) => a.chave)])), [s]);
}

function Caixa({ marcado, parte, desabilitado, rotulo, aoMudar }: { marcado: boolean; parte?: boolean; desabilitado?: boolean; rotulo: string; aoMudar: (v: boolean) => void }) {
  return (
    <input
      type="checkbox"
      className="size-4 shrink-0 cursor-pointer accent-acento-forte disabled:cursor-not-allowed"
      checked={marcado}
      disabled={desabilitado}
      aria-label={rotulo}
      ref={(el) => {
        if (el) el.indeterminate = !!parte;
      }}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => aoMudar(e.target.checked)}
    />
  );
}

/**
 * Val1 (papel por sub-sistema, ficha 2026-10-28): o editor de acesso em árvore, sub-sistema > página > ação. Usado no papel
 * (Papéis) e na exceção por pessoa (Membros, com `base` = a soma dos papéis: o que difere dela aparece com o selo "exceção").
 * Com sub-sistemas ligados no workspace, os grupos seguem a divisão dele; sem, a divisão padrão (a mesma árvore como guia).
 * Val3 (ficha 2026-10-29, item 2): com sub-sistemas ligados (o "recorte"), só o sub-sistema marcado aparece aberto e marcado;
 * os outros, fechados e sem marcação (nenhum "parcial" por página compartilhada); a página compartilhada leva o selo
 * "também em: ..."; o resumo do topo diz quantas páginas abrem e em quais sub-sistemas. `soMarcados` (Meus acessos) esconde
 * o que não abre.
 */
export function ArvoreAcesso({
  permissoes,
  estado,
  aoMudar,
  somenteLeitura,
  base,
  retiradas,
  sujeito = 'papel',
  soMarcados,
}: {
  permissoes: Permissao[];
  estado: EstadoAcesso;
  aoMudar?: (e: EstadoAcesso) => void;
  somenteLeitura?: boolean;
  base?: EstadoAcesso;
  /** Exceção por pessoa: páginas retiradas pela própria exceção (também as que o papel desmarca, rval1-8): selo "exceção". */
  retiradas?: ReadonlySet<string>;
  /** Quem o resumo do topo descreve: o papel (Papéis), a pessoa (exceção) ou você (Minha conta). */
  sujeito?: 'papel' | 'pessoa' | 'voce';
  /** Só o que abre: os sub-sistemas marcados e as páginas marcadas (Meus acessos, somente leitura). */
  soMarcados?: boolean;
}) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const sub = useSubsistema();
  const paginas = usePaginasPorModulo();
  const grupos = useMemo(
    () => gruposDaArvore(paginas, sub.ativo ? sub.config : {}, modulosWeb.flatMap((m) => (m.paginasCasca ?? []).filter((p) => !p.semMenu).map((p) => ({ slug: m.slug, chave: p.chave, pagina: p.pagina })))),
    [paginas, sub.ativo, sub.config],
  );
  // Sem sub-sistemas ligados, a árvore segue a divisão padrão como guia, como antes (decisão do coordenador, 29/10).
  const recorte = sub.ativo;
  const acoes = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of permissoes) if (p.modulo !== 'nucleo') m.set(p.modulo, [...(m.get(p.modulo) ?? []), p.codigo]);
    return m;
  }, [permissoes]);
  const porCodigo = useMemo(() => new Map(permissoes.map((p) => [p.codigo, p])), [permissoes]);
  // Ninguém concede o que não tem: a ação que quem edita não tem só aparece (marcada se já estava), sem mudar.
  const podeConceder = (p: string) => s.pode(p);
  const mudar = (e: EstadoAcesso) => !somenteLeitura && aoMudar?.(e);
  // Fora da árvore: as ações dos módulos sem página no web e as do núcleo (menos as de uso dos sub-sistemas, que são a caixa do grupo).
  const outras = permissoes.filter((p) => (p.modulo === 'nucleo' ? !PERMISSOES_SUBSISTEMAS.includes(p.codigo) : !paginas.has(p.modulo)));
  const excecao = (difere: boolean) => base && difere && <Selo cor="roxo">{t('usuarios.arvore.excecao')}</Selo>;
  const rotuloAcao = (codigo: string) => t(chavesDoRotuloDaAcao(codigo, porCodigo.get(codigo)?.acao ?? codigo), { defaultValue: porCodigo.get(codigo)?.acao ?? codigo });

  // Funções que desenham (não componentes): o `details` aberto não se fecha a cada clique.
  const acao = (codigo: string) => {
    const marcada = estado.permissoes.has(codigo);
    const travada = somenteLeitura || (!marcada && !podeConceder(codigo));
    return (
      <label key={codigo} className={`flex min-w-0 items-center gap-2 text-sm ${travada ? 'text-tinta-3' : 'cursor-pointer'}`} title={!marcada && !podeConceder(codigo) ? t('usuarios.arvore.semConceder') : codigo}>
        <Caixa marcado={marcada} desabilitado={travada} rotulo={rotuloAcao(codigo)} aoMudar={(v) => mudar(alternarAcao(estado, codigo, v, paginas))} />
        <span className="truncate" title={rotuloAcao(codigo)}>
          {rotuloAcao(codigo)}
        </span>
        {excecao(!!base && base.permissoes.has(codigo) !== marcada)}
      </label>
    );
  };

  const nomeGrupo = (chave: string) => (chave === 'comuns' ? t('usuarios.arvore.comuns') : tn(`subsistemas.nomes.${chave}`));

  const grupo = (g: GrupoArvore) => {
    const situacao = estadoDoGrupo(estado, g, recorte);
    const nome = nomeGrupo(g.chave);
    const ativo = !recorte || grupoMarcado(estado, g);
    const visiveis = soMarcados ? g.paginas.filter((p) => paginaMarcadaNoGrupo(estado, g, p.chave, recorte)) : g.paginas;
    const marcadas = g.paginas.filter((p) => paginaMarcadaNoGrupo(estado, g, p.chave, recorte)).length;
    // No recorte, marcar página de sub-sistema não marcado marca o sub-sistema: sem poder concedê-lo, a caixa fica travada.
    const semUsar = recorte && !ativo && g.chave !== 'comuns' && !podeConceder(permissaoDoSubsistema(g.chave));
    return (
      <details key={g.chave} className="rounded-lg border border-borda" data-grupo-acesso={g.chave} data-grupo-marcado={ativo ? '' : undefined} open={recorte && g.chave !== 'comuns' && ativo ? true : undefined}>
        <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-3 py-2 text-sm">
          <Caixa
            marcado={situacao === 'todas'}
            parte={situacao === 'parte'}
            desabilitado={somenteLeitura || semUsar}
            rotulo={nome}
            aoMudar={(v) => mudar(alternarGrupo(estado, g, v, grupos, paginas, acoes, podeConceder))}
          />
          <span className="font-semibold">{nome}</span>
          <span className="text-xs text-tinta-3">{t('usuarios.arvore.contagem', { marcadas, total: g.paginas.length })}</span>
        </summary>
        <ul className="flex flex-col gap-2 border-t border-borda px-3 py-3">
          {visiveis.map((p) => {
            const marcada = paginaMarcadaNoGrupo(estado, g, p.chave, recorte);
            const daBase = base ? paginaMarcadaNoGrupo(base, g, p.chave, recorte) : marcada;
            const outros = recorte ? tambemEm(grupos, g, p.chave) : [];
            const cabeca = (
              <>
                <Caixa
                  marcado={marcada}
                  desabilitado={somenteLeitura || semUsar}
                  rotulo={tn(`${p.slug}:abas.${p.aba}`)}
                  aoMudar={(v) => mudar(alternarPaginaNoGrupo(estado, g, p.chave, v, recorte, paginas, acoes, podeConceder))}
                />
                <span className="min-w-0 break-words">{tn(`${p.slug}:abas.${p.aba}`)}</span>
                <span className="text-xs text-tinta-3">{tn(`${p.slug}:nome`)}</span>
                {outros.length > 0 && (
                  <span data-tambem-em="" className="min-w-0 break-words">
                    <Selo quebra>{t('usuarios.arvore.tambemEm', { lista: outros.map(nomeGrupo).join(', ') })}</Selo>
                  </span>
                )}
                {ativo && excecao(daBase !== marcada || (!marcada && !!retiradas?.has(p.chave)))}
              </>
            );
            return (
              <li key={p.chave} className="min-w-0" data-pagina-acesso={p.chave}>
                {/* No recorte, o sub-sistema não marcado não mostra as ações (são do módulo e já marcadas pelo outro). */}
                {ativo ? (
                  <details>
                    <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-sm">{cabeca}</summary>
                    <div className="mt-2 ml-6 flex flex-col gap-1.5">
                      <p className="text-xs text-tinta-3">{t('usuarios.arvore.acoesDoModulo', { modulo: tn(`${p.slug}:nome`) })}</p>
                      <div className="grid grid-cols-1 gap-x-3 gap-y-1.5 sm:grid-cols-2">
                        {(acoes.get(p.slug) ?? []).map(acao)}
                      </div>
                    </div>
                  </details>
                ) : (
                  <label className={`flex flex-wrap items-center gap-2 text-sm ${somenteLeitura || semUsar ? '' : 'cursor-pointer'}`}>{cabeca}</label>
                )}
              </li>
            );
          })}
        </ul>
      </details>
    );
  };

  const resumo = resumoDoAcesso(estado, grupos, recorte);
  const textoResumo =
    resumo.paginas === 0
      ? t(`usuarios.arvore.resumo.${sujeito}Nenhuma`)
      : t(resumo.subsistemas.length ? 'usuarios.arvore.resumo.comSubsistemas' : 'usuarios.arvore.resumo.semSubsistemas', {
          sujeito: t(`usuarios.arvore.resumo.${sujeito}`),
          paginas: t('usuarios.arvore.resumo.paginas', { count: resumo.paginas }),
          lista: resumo.subsistemas.map(nomeGrupo).join(', '),
        });
  const mostrados = soMarcados ? grupos.filter((g) => (recorte ? grupoMarcado(estado, g) : true) && g.paginas.some((p) => paginaMarcadaNoGrupo(estado, g, p.chave, recorte))) : grupos;

  if (!grupos.length && !outras.length) return <p className="text-sm text-tinta-3">{t('usuarios.papel.semModulos')}</p>;
  return (
    <div className="flex flex-col gap-2" data-arvore-acesso="">
      <p className="text-sm font-semibold text-tinta-1" data-resumo-acesso="" aria-live="polite">
        {textoResumo}
      </p>
      {!soMarcados && <p className="text-xs text-tinta-3">{t(recorte ? 'usuarios.arvore.ajuda' : 'usuarios.arvore.ajudaSemSubsistemas')}</p>}
      {mostrados.map(grupo)}
      {outras.length > 0 && (
        <details className="rounded-lg border border-borda" data-grupo-acesso="outras">
          <summary className="cursor-pointer px-3 py-2 text-sm font-semibold">{t('usuarios.arvore.outras')}</summary>
          <div className="grid grid-cols-1 gap-x-3 gap-y-1.5 border-t border-borda px-3 py-3 sm:grid-cols-2">
            {outras.map((p) => (
              <div key={p.codigo} className="flex min-w-0 flex-col">
                <span className="text-xs text-tinta-3">{p.modulo === 'nucleo' ? t('usuarios.papel.nucleo') : p.modulo_nome}</span>
                {acao(p.codigo)}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
